/**
 * Typed fetch helper for `/api/v1/admin/finance/**`, `/api/v1/admin/merchants/search`
 * and `/api/v1/admin/promotions` — none of these are in the generated OpenAPI
 * `paths` yet. Mirrors `features/payments-sandbox/api.ts` (same auth header,
 * same `ApiError` throw-on-!ok contract) but adds PUT/DELETE since the finance
 * endpoints are full CRUD, not just GET/POST.
 */
import { getAccessToken } from '@favelafood/shared/auth/tokens';
import { ApiError, type ApiErrorPayload } from '@favelafood/shared/api/errors';

const baseUrl = import.meta.env.VITE_API_BASE ?? '';

export type QueryValue = string | number | boolean | null | undefined;
export type QueryParams = Record<string, QueryValue>;

const toQueryString = (query: QueryParams | undefined): string => {
  if (!query) return '';
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === null || value === undefined) continue;
    search.set(key, String(value));
  }
  const qs = search.toString();
  return qs ? `?${qs}` : '';
};

const authHeaders = (): HeadersInit => {
  const headers: Record<string, string> = { Accept: 'application/json' };
  const token = getAccessToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
};

/** Best-effort decode of an error body (mirrors `payments-sandbox/api.ts`). */
const parseErrorPayload = async (res: Response): Promise<ApiErrorPayload> => {
  try {
    const body = (await res.clone().json()) as unknown;
    if (typeof body === 'object' && body !== null) {
      return body as ApiErrorPayload;
    }
  } catch {
    /* fall through to statusText */
  }
  return { message: res.statusText };
};

const ensureOk = async (res: Response): Promise<void> => {
  if (res.ok) return;
  const payload = await parseErrorPayload(res);
  throw new ApiError(res.status, payload, `HTTP ${res.status}`);
};

const parseBody = async <T>(res: Response): Promise<T> => {
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
};

/** GET `/api/v1/admin{path}` and parse the JSON body as `T`. */
export async function adminGet<T>(path: string, query?: QueryParams): Promise<T> {
  const res = await fetch(`${baseUrl}/api/v1/admin${path}${toQueryString(query)}`, {
    method: 'GET',
    headers: authHeaders(),
  });
  await ensureOk(res);
  return parseBody<T>(res);
}

/** POST a JSON `body` to `/api/v1/admin{path}` and parse the JSON response as `T`. */
export async function adminPost<T, B>(path: string, body: B): Promise<T> {
  const res = await fetch(`${baseUrl}/api/v1/admin${path}`, {
    method: 'POST',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  await ensureOk(res);
  return parseBody<T>(res);
}

/** PUT a JSON `body` to `/api/v1/admin{path}` and parse the JSON response as `T`. */
export async function adminPut<T, B>(path: string, body: B): Promise<T> {
  const res = await fetch(`${baseUrl}/api/v1/admin${path}`, {
    method: 'PUT',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  await ensureOk(res);
  return parseBody<T>(res);
}

/** DELETE `/api/v1/admin{path}` (204 No Content — nothing parsed). */
export async function adminDelete(path: string): Promise<void> {
  const res = await fetch(`${baseUrl}/api/v1/admin${path}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  await ensureOk(res);
}
